import { useCallback } from "react";

import { Action, Stack } from "types/generated";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";
import useHasStackAdminAccess from "./useHasStackAdminAccess";

const DRIFT_DETECTION_DELETE_ACCESS_TYPES = [Action.DriftDetectionIntegrationDelete];

const hasDriftDetectionDeleteAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(DRIFT_DETECTION_DELETE_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can delete drift detection integrations.
 * A user can delete drift detection if they have the delete permission or if they have admin access to the stack's space.
 * Stacks created from a template deployment cannot have drift detection deleted.
 * @returns {(stack?: Pick<Stack, "spaceDetails" | "space" | "isLockedByTemplateDeployment">) => AccessResult} Function that takes a stack and returns AccessResult
 */
const useCanDeleteDriftDetection = (): ((
  stack?: Pick<Stack, "spaceDetails" | "space" | "isLockedByTemplateDeployment">
) => AccessResult) => {
  const hasAdminAccess = useHasStackAdminAccess();
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (stack) => {
      if (!stack) {
        return { result: false };
      }

      const templateDeploymentCheck = checkTemplateDeploymentAccess(stack);
      if (templateDeploymentCheck) {
        return templateDeploymentCheck;
      }

      const hasAccess =
        hasAdminAccess(stack).result ||
        hasDriftDetectionDeleteAccess(viewer, stack.spaceDetails?.id || stack.space);

      if (hasAccess) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [hasAdminAccess, viewer]
  );
};

export default useCanDeleteDriftDetection;
